"use client";

import React from "react";
import { Section } from "@/components/ui/section";
import { m as motion } from "framer-motion";
import { EXPERIENCE_DATA, SECTION_METADATA } from "@/lib/data";

interface ExperienceItem {
  role: string;
  company: string;
  period: string;
  description: string;
  skills: string[];
}

// --- WEB COMPONENTS (Original Layout) ---
const ExperienceCardWeb = React.memo(({ exp }: { exp: ExperienceItem }) => {
  return (
    <div className="p-8 rounded-[2rem] bg-card/40 border border-border/40 hover:border-primary/40 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.3)] transition-all duration-500 backdrop-blur-md group">
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
            {exp.role}
          </h3>
          <span className="text-xs font-mono font-bold text-muted-foreground/50 tracking-widest">
            {exp.period}
          </span>
        </div>
        <p className="text-base text-primary/80 font-serif italic">
          {exp.company}
        </p>
      </div>
      <p className="text-muted-foreground/80 leading-relaxed mb-6 text-sm sm:text-base">
        {exp.description}
      </p>
      <div className="flex flex-wrap gap-2">
        {exp.skills.map((skill) => (
          <span
            key={skill}
            className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-muted-foreground/60 border border-border/20 px-3 py-1 rounded-full bg-secondary/20 group-hover:bg-primary/5 group-hover:border-primary/20 transition-all duration-300"
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
});
ExperienceCardWeb.displayName = "ExperienceCardWeb";

// --- MOBILE COMPONENTS ---
const ExperienceCardMobile = ({
  exp,
  index,
}: {
  exp: ExperienceItem;
  index: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative pl-8 pb-12 last:pb-4"
    >
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-border/50 to-transparent">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-background border-2 border-primary shadow-[0_0_10px_rgba(var(--primary),0.5)]" />
      </div>
      
      <div className="space-y-4">
        <div className="flex flex-col gap-1 text-left">
          <h3 className="text-lg font-bold text-foreground leading-tight">
            {exp.role}
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-primary/80 font-serif italic">
              {exp.company}
            </p>
            <span className="text-[10px] font-mono font-bold text-muted-foreground/40 tracking-widest uppercase">
              {exp.period}
            </span>
          </div>
        </div>
        
        <div className="p-5 rounded-2xl bg-card/40 border border-border/50 backdrop-blur-md shadow-sm">
          <p className="text-[13px] sm:text-sm text-muted-foreground/80 leading-relaxed text-left">
            {exp.description}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {exp.skills.map((skill) => (
            <span
              key={skill}
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 border border-border/20 px-2.5 py-1 rounded-full bg-secondary/10"
            >
              {skill}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default function Experience({ data }: { data?: any }) {
  const experiences = (data || EXPERIENCE_DATA).map((exp: any) => ({
    role: exp.role,
    company: exp.company,
    period: exp.period,
    description: exp.description,
    skills: exp.skills || [],
  }));

  return (
    <Section id="experience" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Common Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-24"
        >
          <h2 className="text-[9px] md:text-xs font-mono uppercase tracking-[0.4em] text-muted-foreground mb-3 opacity-70">
            {SECTION_METADATA.experience.sectionNumber} /{" "}
            {SECTION_METADATA.experience.sectionLabel}
          </h2>
          <h3 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-5 md:mb-6 px-2">
            {SECTION_METADATA.experience.title}
          </h3>
          <p className="text-muted-foreground/80 max-w-2xl mx-auto text-xs sm:text-base md:text-lg leading-relaxed px-4">
            {SECTION_METADATA.experience.description}
          </p>
        </motion.div>

        {/* --- WEB VIEW (Hidden on Mobile) --- */}
        <div className="hidden md:block relative max-w-4xl mx-auto">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2" />
          <div className="space-y-20">
            {experiences.map((exp: ExperienceItem, index: number) => (
              <div
                key={index}
                className="relative grid grid-cols-[1fr_auto_1fr] gap-8"
              >
                {index % 2 === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="pr-16"
                  >
                    <ExperienceCardWeb exp={exp} />
                  </motion.div>
                ) : (
                  <div />
                )}

                <div className="relative flex justify-center">
                  <div className="relative z-10 w-4 h-4 bg-background border-4 border-primary rounded-full shadow-lg shadow-primary/20" />
                </div>

                {index % 2 !== 0 ? (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="pl-16"
                  >
                    <ExperienceCardWeb exp={exp} />
                  </motion.div>
                ) : (
                  <div />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* --- MOBILE VIEW (Hidden on Desktop) --- */}
        <div className="md:hidden flex flex-col w-full max-w-md mx-auto px-1 sm:px-0">
          {experiences.map((exp: ExperienceItem, index: number) => (
            <ExperienceCardMobile key={index} exp={exp} index={index} />
          ))}
        </div>
      </div>
    </Section>
  );
}
